import type { MutableRefObject, Ref, RefCallback } from 'react';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Custom hook to merge multiple refs (function or object refs) into a single callback ref.
 * Useful when you need to assign the same DOM node to multiple refs.
 *
 * @template T - The element type being referenced.
 * @param refs - Any number of refs (object or function).
 * @returns A single callback ref to assign to a DOM element.
 *
 * @example
 * const ref1 = useRef(null);
 * const ref2 = useRef(null);
 * const mergedRef = useMergeRefs([ref1, ref2]);
 * return <div ref={mergedRef} />;
 *
 * @copyright Hook extracted from `@floating-ui` library with some minor tweaks.
 */
export function useMergeRefs<Instance>(
  refs: Array<Ref<Instance> | undefined>
): null | RefCallback<Instance> {
  const cleanupRef = useRef<void | (() => void)>(undefined);

  const refEffect = useCallback((instance: Instance | null) => {
    const cleanups = refs.map((ref) => {
      if (ref == null) return ref;

      if (typeof ref === 'function') {
        const refCallback = ref;
        const refCleanup: void | (() => void) = refCallback(instance);
        return typeof refCleanup === 'function'
          ? refCleanup
          : () => {
              refCallback(null);
            };
      }

      (ref as MutableRefObject<Instance | null>).current = instance;
      return () => {
        (ref as MutableRefObject<Instance | null>).current = null;
      };
    });

    return () => {
      cleanups.forEach((refCleanup) => refCleanup?.());
    };
    // Rule of hooks does not understand that `refs` is a stable array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refs);

  return useMemo(() => {
    if (refs.every((ref) => ref == null)) {
      return null;
    }

    return (value) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        (cleanupRef as MutableRefObject<void | (() => void)>).current = undefined;
      }

      if (value != null) {
        (cleanupRef as MutableRefObject<void | (() => void)>).current = refEffect(value);
      }
    };
    // Rule of hooks does not understand that `refs` is a stable array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refs);
}
